import { EMPTY_ARRAY, isSpot, useSecurity, type ResolutionEnumNoPrefix, type Security } from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-community';
import { createContext, memo, useContext, useMemo, type PropsWithChildren } from 'react';
import { useHydrateDeepDiveMarkets } from '../containers/Trading/DeepDive/useHydrateDeepDiveMarkets';
import { useCombinedMarketsAndAccounts } from '../hooks/useCombinedMarketsAndAccounts';
import { useExchangeMarkets } from '../hooks/useMarketsOfType';

export const DeepDiveContext = createContext<DeepDiveContextProps | null>(null);
DeepDiveContext.displayName = 'DeepDiveContext';

interface DeepDiveContextProps extends DeepDiveProviderProps {
  security: Security | undefined;
  selectedExchangeMarkets: string[];
  selectedCombined: string[];
  securityExchangeMarkets: string[];
  canChangeMarkets: boolean;
}

export const useDeepDiveContext = () => {
  const context = useContext(DeepDiveContext);
  if (context === null) {
    throw new Error('Missing DeepDiveContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export type DeepDiveMarketSelection = {
  marketAccounts: string[];
  markets: string[];
  aggregation: string | undefined;
};

export type DeepDiveProviderProps = {
  symbol: string | undefined;
  setSymbol: (symbol: string) => void;
  selectedMarketAccounts: string[];
  selectedMarkets: string[];
  setShowMarkets: (showMarkets: boolean) => void;
  selectedAggregation?: string;
  setMarketSelection: (selection: DeepDiveMarketSelection) => void;
  showMarkets: boolean;
  setChartResolution: (chartResolution: ResolutionEnumNoPrefix) => void;
  chartResolution: ResolutionEnumNoPrefix | undefined;
  closeDeepDive: () => void;
  setPriceIncrement: (priceIncrement: string) => void;
  setShowCumQty: (showCumQty: boolean) => void;
  priceIncrement: string | undefined;
  showCumQty: boolean | undefined;
  annotations: AgAnnotation[] | undefined;
  chartType: AgPriceVolumeChartType | undefined;
  setAnnotations: (annotations: AgAnnotation[] | undefined) => void;
  setChartType: (chartType: AgPriceVolumeChartType) => void;
};

export const DeepDiveProvider = memo(
  ({
    symbol,
    selectedMarketAccounts,
    selectedMarkets,
    children,
    ...props
  }: PropsWithChildren<DeepDiveProviderProps>) => {
    const security = useSecurity(symbol);

    useHydrateDeepDiveMarkets({
      symbol,
      selectedMarketAccounts,
      selectedMarkets,
      selectedAggregation: props.selectedAggregation,
      setMarketSelection: props.setMarketSelection,
    });

    const selectedCombined = useCombinedMarketsAndAccounts(selectedMarketAccounts, selectedMarkets);

    const selectedExchangeMarkets = useExchangeMarkets(selectedCombined, false);
    const securityExchangeMarkets = useExchangeMarkets(security?.Markets ?? EMPTY_ARRAY, true);

    // https://app.shortcut.com/talostrading/story/58293/hide-markets-panel-bar-toggle-on-perps-futures
    const canChangeMarkets = useMemo(() => {
      return Boolean(security && isSpot(security));
    }, [security]);

    return (
      <DeepDiveContext.Provider
        value={{
          symbol,
          security,
          selectedMarketAccounts,
          selectedMarkets,
          selectedExchangeMarkets,
          selectedCombined,
          securityExchangeMarkets,
          canChangeMarkets,
          ...props,
        }}
      >
        {children}
      </DeepDiveContext.Provider>
    );
  }
);
